import React from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { getImageUrl, getBaseUrl, isRootDomain } from '@/lib/utils';
import { useTenant } from '@/contexts/TenantContext';
import StickyNavigation from "@/components/layout/StickyNavigation";
import {
  ArrowRight,
  CheckCircle,
  Shield,
  Phone,
  Mail,
  MapPin,
  Star,
  Users,
  Clock,
  Award
} from "lucide-react";

interface ProductPageProps {
  // SEO Meta Data
  title: string;
  metaDescription: string;

  // Hero Section
  heroTitle: string;
  heroSubtitle: string;
  heroDescription: string;
  heroImage?: string;

  // Key Benefits
  keyBenefits: string[];

  // Features Section
  features: Array<{
    title: string;
    description: string;
    icon: React.ReactNode;
  }>;

  // Coverage Details
  coverageDetails: Array<{
    title: string;
    description: string;
    included: string[];
  }>;

  // Why Choose Us
  whyChooseUs: Array<{
    title: string;
    description: string;
  }>;

  // FAQ Section
  faqs: Array<{
    question: string;
    answer: string;
  }>;

  // CTA Section
  ctaTitle: string;
  ctaDescription: string;
  primaryCta: string;
  secondaryCta?: string;

  // Product ID for quote form
  productId: string;
}

export const ProductPageTemplate = ({
  title,
  metaDescription,
  heroTitle,
  heroSubtitle,
  heroDescription,
  heroImage,
  keyBenefits,
  features,
  coverageDetails,
  whyChooseUs,
  faqs,
  ctaTitle,
  ctaDescription,
  primaryCta,
  secondaryCta,
  productId
}: ProductPageProps) => {
  const navigate = useNavigate();
  const { tenant } = useTenant();

  // Tenant path prefix detection for path-based routing
  const subMatch = window.location.pathname.match(/^(?:\/business)?\/site\/([^/]+)/);
  const subPrefix = subMatch ? `/site/${subMatch[1]}` : '';
  const tLink = (path: string) => `${subPrefix}${path}`;
  const isDCC = (tenant?.subdomain === 'dubaicommercity') || (subMatch ? subMatch[1] === 'dubaicommercity' : false);
  const isVirtuzone = (tenant?.subdomain === 'virtuzone') || (subMatch ? subMatch[1] === 'virtuzone' : false) || (typeof window !== 'undefined' ? (window.location.hostname.split('.')[0] === 'virtuzone') : false);

  // Footer logo size: 70% of navbar logo (context-aware)
  const isMainSite = isRootDomain();
  const host = typeof window !== 'undefined' ? window.location.hostname : '';
  const hostSub = host.split('.')[0] || '';
  const pathIsIncorpify = typeof window !== 'undefined' && /(?:^|\/)site\/incorpify-ai(?:\/|$)/.test(window.location.pathname);
  const isIncorpifyAI = (tenant?.subdomain === 'incorpify-ai') || (hostSub === 'incorpify-ai') || pathIsIncorpify;
  const footerLogoDesktop = isMainSite ? 'h-[8.4rem]' : (isIncorpifyAI ? 'h-[8.064rem]' : 'h-[4.2rem]');
  const footerLogoMobile = isMainSite ? 'h-24' : (isIncorpifyAI ? 'h-[6.048rem]' : 'h-[3.36rem]');

  // Helper function to navigate and scroll to top
  const navigateToPage = (path: string) => {
    navigate(tLink(path));
    window.scrollTo(0, 0);
  };

  // Set document title and meta description
  React.useEffect(() => {
    document.title = title;

    // Update meta description
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute('content', metaDescription);
    } else {
      const meta = document.createElement('meta');
      meta.name = 'description';
      meta.content = metaDescription;
      document.head.appendChild(meta);
    }
  }, [title, metaDescription]);

  return (
    <div className="min-h-screen bg-white">
      <StickyNavigation />

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-50 to-indigo-100 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center min-h-[300px]">
            <div>
              <Badge variant="secondary" className="mb-4">
                {heroSubtitle}
              </Badge>
              <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
                {heroTitle}
              </h1>
              <p className="text-xl text-gray-600 mb-8">
                {heroDescription}
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button
                  size="lg"
                  className="hover:opacity-90"
                  style={{
                    backgroundColor: tenant?.colorScheme?.startMatrixButtonColor || undefined,
                    color: tenant?.colorScheme?.startMatrixButtonColor ? '#FFFFFF' : undefined
                  }}
                  onClick={() => navigate(tLink(`/quote/${productId}`))}
                >
                  {primaryCta}
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
                {secondaryCta && (
                  <Button
                    size="lg"
                    variant="outline"
                    onClick={() => navigateToPage('/decision-tree')}
                  >
                    {secondaryCta}
                  </Button>
                )}
              </div>
            </div>
            {heroImage && (
              <div className="lg:text-right flex items-center justify-center h-full">
                <img
                  src={heroImage}
                  alt={heroTitle}
                  className="max-w-[350px] max-h-[280px] object-contain mx-auto lg:mx-0"
                />
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Key Benefits */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Key Benefits</h2>
            <p className="text-xl text-gray-600">Why choose our insurance coverage</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {keyBenefits.map((benefit, index) => (
              <div key={index} className="flex items-start space-x-3">
                <CheckCircle className="h-6 w-6 text-green-500 mt-1 flex-shrink-0" />
                <p className="text-gray-700">{benefit}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Features & Coverage</h2>
            <p className="text-xl text-gray-600">Comprehensive protection for your business</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <Card key={index}>
                <CardHeader>
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      {feature.icon}
                    </div>
                    <CardTitle className="text-lg">{feature.title}</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Coverage Details */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">What's Covered</h2>
            <p className="text-xl text-gray-600">Detailed coverage information</p>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {coverageDetails.map((coverage, index) => (
              <Card key={index}>
                <CardHeader>
                  <CardTitle>{coverage.title}</CardTitle>
                  <CardDescription>{coverage.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    {coverage.included.map((item, itemIndex) => (
                      <li key={itemIndex} className="flex items-start space-x-2">
                        <CheckCircle className="h-4 w-4 text-green-500 mt-1 flex-shrink-0" />
                        <span className="text-sm text-gray-700">{item}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16 bg-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Why Choose Yalla Compare</h2>
            <p className="text-xl text-gray-600">Your trusted insurance partner in the UAE</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {whyChooseUs.map((reason, index) => (
              <div key={index} className="text-center">
                <div className="flex justify-center mb-4">
                  {index === 0 && <Award className="h-12 w-12 text-blue-600" />}
                  {index === 1 && <Users className="h-12 w-12 text-blue-600" />}
                  {index === 2 && <Clock className="h-12 w-12 text-blue-600" />}
                  {index === 3 && <Star className="h-12 w-12 text-blue-600" />}
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{reason.title}</h3>
                <p className="text-gray-600">{reason.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section - tenant aware bottom banner */}
      <section
        className="py-16"
        style={{
          backgroundColor: isDCC
            ? '#ffffff'
            : (isVirtuzone
              ? '#f2f3ee' // force Virtuzone light bottom banner
              : (tenant?.bannerConfig?.bottomBannerBackgroundColor || tenant?.bannerConfig?.backgroundColor || '#f2f3ee')),
          color: (isDCC || isVirtuzone) ? '#111827' : (tenant?.bannerConfig?.bottomBannerTextColor || '#111827')
        }}
      >
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-4" style={{ color: isDCC ? '#111827' : (tenant?.bannerConfig?.bottomBannerTextColor || '#111827') }}>{ctaTitle}</h2>
          <p className="text-xl mb-8" style={{ color: isDCC ? '#374151' : (tenant?.bannerConfig?.bottomBannerTextColor || '#111827') }}>{ctaDescription}</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              size="lg"
              className="hover:opacity-90"
              style={{
                backgroundColor: tenant?.colorScheme?.startMatrixButtonColor || undefined,
                color: tenant?.colorScheme?.startMatrixButtonColor ? '#FFFFFF' : undefined
              }}
              onClick={() => navigate(tLink(`/quote/${productId}`))}
            >
              {primaryCta}
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="bg-white text-blue-600 hover:bg-gray-50"
              onClick={() => navigateToPage('/decision-tree')}
            >
              Compare All Products
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-white text-gray-900 py-12 border-t">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <div className="cursor-pointer mb-4 flex items-center" onClick={() => navigateToPage("/")}>
                <img
                  src={(tenant?.logoUrl) || getImageUrl("yallacompare-for-business-logo.png")}
                  alt={tenant?.name || "YallaCompare for Business"}
                  className={`w-auto object-contain ${footerLogoMobile} sm:${footerLogoDesktop}`}
                />
              </div>
              <p className="text-gray-600">
                Your trusted partner for business insurance in the UAE.
              </p>
            </div>

            <div>
              <h4 className="text-lg font-semibold mb-4 text-gray-900">Contact</h4>
              <div className="space-y-2 text-gray-600">
                <div className="flex items-center">
                  <Phone className="h-4 w-4 mr-2" />
                  <span>+971586948688</span>
                </div>
                <div className="flex items-center">
                  <Mail className="h-4 w-4 mr-2" />
                  <span>business@yallacompare.com</span>
                </div>
                <div className="flex items-center">
                  <MapPin className="h-4 w-4 mr-2" />
                  <span>Dubai, UAE</span>
                </div>
              </div>
            </div>

            <div>
              <h4 className="text-lg font-semibold mb-4 text-gray-900">Products</h4>
              <ul className="space-y-2 text-gray-600">
                <li><button onClick={() => navigate(tLink('/products/property-insurance'))} className="hover:text-gray-900">Property Insurance</button></li>
                <li><button onClick={() => navigate(tLink('/products/liability-coverage'))} className="hover:text-gray-900">Liability Coverage</button></li>
                <li><button onClick={() => navigate(tLink('/products/employee-benefits'))} className="hover:text-gray-900">Employee Benefits</button></li>
                <li><button onClick={() => navigate(tLink('/products/cyber-protection'))} className="hover:text-gray-900">Cyber Protection</button></li>
                <li><button onClick={() => navigate(tLink('/products/fleet-insurance'))} className="hover:text-gray-900">Fleet Insurance</button></li>
                <li><button onClick={() => navigate(tLink('/products/professional-indemnity'))} className="hover:text-gray-900">Professional Indemnity</button></li>
                <li><button onClick={() => navigate(tLink('/products/business-interruption'))} className="hover:text-gray-900">Business Interruption</button></li>
                <li><button onClick={() => navigate(tLink('/products/workers-compensation'))} className="hover:text-gray-900">Workers' Compensation</button></li>
                <li><button onClick={() => navigate(tLink('/products/product-liability'))} className="hover:text-gray-900">Product Liability</button></li>
              </ul>
            </div>

            <div>
              <h4 className="text-lg font-semibold mb-4 text-gray-900">Company</h4>
              <ul className="space-y-2 text-gray-600">
                <li><button onClick={() => navigate(tLink('/about-us'))} className="hover:text-gray-900">About Us</button></li>
                <li><button onClick={() => navigate(tLink('/contact-us'))} className="hover:text-gray-900">Contact</button></li>
                <li><button onClick={() => navigate(tLink('/privacy-policy'))} className="hover:text-gray-900">Privacy Policy</button></li>
                <li><button onClick={() => navigate(tLink('/terms-and-conditions'))} className="hover:text-gray-900">Terms and Conditions</button></li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-200 mt-8 pt-8 text-center text-gray-600">
            <p>&copy; 2026 Yalla Compare. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};
